import React, { useState, useEffect, useMemo, useRef, ChangeEvent, FormEvent, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import orgService from '../services/orgService';
import { mappingApi } from '../services/mappingApi';
import { TypeProcMap } from '../types';
import './TypeMapping.css';

interface MappingRow {
  key: string;
  endevorType: string;
  processorGroup: string;
  extension: string;
  folder: string;
}

const TypeMapping: React.FC = () => {
  const navigate = useNavigate();
  const [typeProcMap, setTypeProcMap] = useState<TypeProcMap>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = (): boolean => orgService.getRole() === 'admin';

  const rows: MappingRow[] = useMemo(() => {
    return Object.entries(typeProcMap).map(([key, entry]) => {
      const [endevorType = '', processorGroup = ''] = key.split('|');
      return {
        key,
        endevorType,
        processorGroup,
        extension: entry.extension,
        folder: entry.folder,
      };
    });
  }, [typeProcMap]);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(
      (r) =>
        r.endevorType.toLowerCase().includes(term) ||
        r.processorGroup.toLowerCase().includes(term) ||
        r.extension.toLowerCase().includes(term) ||
        r.folder.toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      setError(null);
      setDeleteError(null);
      setDeleteSuccess(false);
      const data = await mappingApi.getMappings();
      setTypeProcMap(data.type_proc_map ?? {});
    } catch (err: any) {
      console.error('Error fetching mappings:', err);
      // If no mappings document exists, treat as empty configuration instead of an error
      if (err?.response?.status === 404) {
        setTypeProcMap({});
        setError(null);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to load mapping configuration.');
        setTypeProcMap({});
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin()) return;
    fetchMappings();
  }, []);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setUploadFile(file ?? null);
    setUploadError(null);
    setUploadSuccess(false);
    setDeleteSuccess(false);
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setUploadFile(file);
      setUploadError(null);
      setUploadSuccess(false);
      setDeleteSuccess(false);
    }
  };

  const clearFile = () => {
    setUploadFile(null);
    setUploadError(null);
    setUploadSuccess(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!uploadFile) {
      setUploadError('Please select an Excel file.');
      return;
    }
    try {
      setUploading(true);
      setUploadError(null);
      setUploadSuccess(false);
      setDeleteSuccess(false);
      await mappingApi.uploadMappings(uploadFile);
      setUploadSuccess(true);
      setUploadFile(null);
      (e.target as HTMLFormElement).reset();
      await fetchMappings();
    } catch (err: any) {
      setUploadError(err.response?.data?.message || err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (!deleting) setShowDeleteModal(false);
  };

  const handleDeleteConfirm = async () => {
    if (!rows.length || deleting) return;

    try {
      setDeleting(true);
      setDeleteError(null);
      setDeleteSuccess(false);
      setUploadSuccess(false);
      await mappingApi.deleteMappings();
      setTypeProcMap({});
      setSearchTerm('');
      setDeleteSuccess(true);
      setShowDeleteModal(false);
    } catch (err: any) {
      // If already missing, just treat as success/empty
      if (err?.response?.status === 404) {
        setTypeProcMap({});
        setSearchTerm('');
        setDeleteSuccess(true);
        setDeleteError(null);
        setShowDeleteModal(false);
      } else {
        setDeleteError(err.response?.data?.message || err.message || 'Failed to delete mapping configuration.');
      }
    } finally {
      setDeleting(false);
    }
  };

  if (!isAdmin()) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  return (
    <div className="type-mapping-container">
      <div className="type-mapping-header">
        <button className="back-button" type="button" onClick={() => navigate('/dashboard/migration')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
          </svg>
          Back
        </button>
        <h1>Type Mapping</h1>
      </div>

      <div className="type-mapping-upload-section">
        <h2 className="section-title">Upload mapping file</h2>
        <p className="section-description">
          Upload an Excel file to update how files are mapped to folder structure during migration. The file defines Endevor type, processor group, file extension, and target folder.
        </p>
        <form className="upload-form" onSubmit={handleUpload}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={uploading}
            className="upload-file-input-hidden"
            aria-label="Choose Excel file"
          />
          <div
            className={`upload-dropzone ${dragActive ? 'upload-dropzone-active' : ''} ${uploadFile ? 'upload-dropzone-has-file' : ''}`}
            onClick={() => !uploadFile && fileInputRef.current?.click()}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploadFile ? (
              <div className="upload-file-selected">
                <span className="upload-file-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
                  </svg>
                </span>
                <span className="upload-file-name" title={uploadFile.name}>{uploadFile.name}</span>
                <button type="button" className="upload-file-remove" onClick={(e) => { e.stopPropagation(); clearFile(); }} disabled={uploading} aria-label="Remove file">
                  Remove
                </button>
              </div>
            ) : (
              <>
                <span className="upload-dropzone-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                  </svg>
                </span>
                <span className="upload-dropzone-text">Choose file or drag and drop</span>
                <span className="upload-dropzone-hint">Excel files only (.xlsx, .xls)</span>
              </>
            )}
          </div>
          {uploadFile && (
            <div className="upload-submit-row">
              <button type="submit" className="upload-button" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload file'}
              </button>
            </div>
          )}
          {uploadError && <div className="upload-error">{uploadError}</div>}
          {uploadSuccess && <div className="upload-success">Mapping file uploaded successfully. Configuration updated.</div>}
        </form>
      </div>

      <div className="type-mapping-content">
        <h2 className="section-title">Current configuration</h2>
        {deleteError && (
          <div className="upload-error" style={{ marginBottom: '8px' }}>
            {deleteError}
          </div>
        )}
        {deleteSuccess && rows.length === 0 && (
          <div className="upload-success" style={{ marginBottom: '8px' }}>
            Mapping configuration deleted successfully.
          </div>
        )}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>Loading configuration...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button type="button" className="retry-button" onClick={fetchMappings}>
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor" />
              </svg>
            </div>
            <div className="empty-text">
              <div className="empty-title">No mapping configuration</div>
              <div className="empty-subtitle">Upload an Excel file above to define type mappings.</div>
            </div>
          </div>
        ) : (
          <>
            <div className="table-toolbar">
              <input
                type="text"
                placeholder="Search by type, processor, extension, or folder..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <span className="mapping-count">
                {filteredRows.length === rows.length
                  ? `${rows.length} mapping(s)`
                  : `${filteredRows.length} of ${rows.length} mapping(s)`}
              </span>
              {rows.length > 0 && (
                <button
                  type="button"
                  className="upload-button"
                  onClick={openDeleteModal}
                  disabled={deleting}
                >
                  Delete configuration
                </button>
              )}
            </div>
            <div className="mappings-table-container">
              <div className="table-wrapper">
                <table className="mappings-table">
                  <thead>
                    <tr className="table-header">
                      <th className="mapping-header-cell">Endevor Type</th>
                      <th className="mapping-header-cell">Processor Group</th>
                      <th className="mapping-header-cell">Extension</th>
                      <th className="mapping-header-cell">Folder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.key} className="table-row">
                        <td className="table-cell type-cell">{row.endevorType}</td>
                        <td className="table-cell proc-cell">{row.processorGroup}</td>
                        <td className="table-cell ext-cell">{row.extension}</td>
                        <td className="table-cell folder-cell">{row.folder}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {showDeleteModal && (
        <div
          className="delete-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDeleteModal();
          }}
        >
          <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h3 id="delete-modal-title">Delete mapping configuration</h3>
              <button
                type="button"
                className="delete-modal-close"
                onClick={closeDeleteModal}
                aria-label="Close"
                disabled={deleting}
              >
                ×
              </button>
            </div>
            <div className="delete-modal-body">
              <p>Are you sure you want to delete the current mapping configuration? This action cannot be undone.</p>
              {deleteError && <div className="delete-modal-error">{deleteError}</div>}
            </div>
            <div className="delete-modal-actions">
              <button
                type="button"
                className="delete-modal-secondary"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-modal-primary"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TypeMapping;
